/**
 * Lets `CollaborativeEditor` instances opt in to AI editing via an `aiMetadata`
 * prop and gives the AI chat handler a single hook to (a) read the editor's
 * current state and (b) stage AI-proposed edits as inline decorations.
 *
 * The registry is held in a ref so editors can register on mount without
 * triggering re-renders of unrelated consumers. The chat-side dispatcher reads
 * through the same ref so it always sees the latest mounted editors.
 */
import type {
  CitationCheckDroppedEntry,
  EditorOperation,
} from '@seen/ai-agents'
import { featureFlags } from '@seen/core/featureFlags'
import type { JsonObject, JsonValue } from '@seen/core/json'
import {
  isBlankFillText,
  placeholderContaining,
  removedNonPlaceholderText,
} from '@seen/core/models/care-plans/fillOnly'
import {
  type PersistedEditorOperation,
  revisableContentField,
  reviseOpContent,
} from '@seen/core/models/compositionNote'
import { isVersionedReferenceString } from '@seen/core/references'
import { generateHTML, generateJSON, type JSONContent } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import type { Editor } from '@tiptap/react'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'
import {
  AI_PROPOSALS_PLUGIN_KEY,
  type AIProposal,
  type ProposalRenderSpec,
  toAIProposal,
} from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import {
  type CitationCheckDroppedReport,
  citationCheckDroppedOf,
  citationCheckDroppedReport,
  citationCheckDroppedSummary,
  survivingPositions,
} from '#src/components/rich-text-editor/citationCheckDropped.ts'
import { escapeHtml } from '#src/components/rich-text-editor/conditionNoteHtml.ts'
import {
  appendedHtml,
  isOneList,
  keepingLabelColon,
} from '#src/components/rich-text-editor/fillReplacement.ts'
import { withAuthoredMentionLabels } from '#src/components/rich-text-editor/mention/authoredMentionLabel.ts'
import {
  citationIssue,
  citationOf,
} from '#src/components/rich-text-editor/suggestionCitation.ts'
import { contractForNode } from '#src/components/rich-text-editor/suggestionContracts.ts'
import type { AnchorFailureKind } from '#src/components/rich-text-editor/suggestionDropReasons.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

export interface CollaborativeEditorAIMetadata {
  /** Stable identifier used by the AI to address this editor. */
  id: string
  /** Human-readable label surfaced to the AI ("Care plan description"). */
  label: string
  /** FHIR reference string for the resource the editor binds to. */
  resourceRef?: string
  /** Maps to `CollaborativeEditor.field`. */
  field?: string
  /** Free-text description the AI sees alongside the label. */
  description?: string
  /** Mirrors useIsReadOnly() + Liveblocks canWrite — registry refuses edits when true. */
  readOnly: boolean
}

interface RegistryEntry {
  editor: Editor
  meta: CollaborativeEditorAIMetadata
  /**
   * Fired after live staging paints at least one proposal into this editor, so
   * the host owning the deferred-reveal state can reveal a still-deferred note
   * rather than leave it half-painted (new proposals visible, pre-existing
   * staged ones hidden behind the banner). Absent for editors with no reveal
   * apparatus.
   */
  onProposalsStaged?: () => void
}

/* Structural mirror of @seen/ai-agents' ReadEditorChoiceField (the zod
 * schema lives in readEditor.ts; not re-exported from the package root, which
 * teardown-phase1 owns). Kept assignable to ReadEditorOutput by shape — the
 * useAIChat fulfillment assigns a ReadEditorResult straight into the tool's
 * ReadEditorOutput, so this must match the schema field-for-field. */
export interface ReadEditorChoiceField {
  /** Durable opaque handle; null only if the field never got one. */
  fieldId: string | null
  /** Allowed values, parsed from data-options. */
  options: string[]
  /** Current display text, or null if empty. */
  currentValue: string | null
  /** Label / sibling text in the same block (agent context). */
  nearbyText: string
  /** Nearest enclosing H1 or H2 section title. */
  heading: string
}

/**
 * A targetable taskItem checkbox in document order. TaskItem nodes are block-
 * level elements invisible to collectChoiceFields — this interface is the only
 * surface that exposes them. Targeting uses section + normalized label because
 * existing notes do not carry created fieldIds on taskItems.
 */
export interface ReadEditorTaskItem {
  /** Normalized label text (trimmed, whitespace-collapsed) of the taskItem. */
  label: string
  /** Whether the checkbox is currently checked. */
  checked: boolean
  /** Nearest enclosing H1 or H2 section heading above this taskItem. */
  heading: string
}

export interface ReadEditorResult {
  id: string
  meta: CollaborativeEditorAIMetadata
  html: string
  markdown: string
  /** Every targetable choice field in document order. */
  fields: ReadEditorChoiceField[]
  /** Every taskItem checkbox in document order, for setTaskItemChecked targeting. */
  taskItems: ReadEditorTaskItem[]
  hasPendingProposals: boolean
}

export interface OperationFailure {
  operation: EditorOperation
  reason: string
}

/**
 * A successfully-staged proposal's persistable data: the decoration id, the
 * SEMANTIC input op (NOT resolved positions, which don't survive y-prosemirror
 * remaps), and resolveOperation's display label. The chat hook maps these into
 * the note's staging extension via `useSuggestionPersistence` so the registry
 * itself stays FHIR-free (design Q5).
 */
export interface StagedProposalRecord {
  /** == decoration id (`${toolCallId}-${idx}`). */
  id: string
  /**
   * The semantic op for hydration re-resolution. Verbatim for every op the
   * model sent EXCEPT a revision, which is persisted as the op it replaced with
   * its prose swapped: a `reviseProposal` names a record that is superseded by
   * the time anything re-reads it, so persisting it verbatim would leave the
   * revision unresolvable on the next load.
   */
  op: PersistedEditorOperation
  /** Display label for when the live decoration is gone. */
  label?: string
  /**
   * The id this record replaces, present only on a revision. The persistence
   * layer moves that record into the superseded array in the SAME write that
   * stages this one, so no reload can observe both as pending.
   */
  supersedes?: string
}

export interface StageProposalsResult {
  ok: boolean
  editorId: string
  staged: number
  failed: OperationFailure[]
  summary: string
  /* The operations the server's citation check dropped from this call before
   * it reached the browser, echoed so the model can tell the clinician. Absent
   * when nothing was dropped. */
  citationCheckDropped?: CitationCheckDroppedReport[]
  /**
   * The staged proposals' persistable records (empty when nothing staged).
   * Built from the INPUT ops so the persisted op round-trips through hydration.
   */
  persisted: StagedProposalRecord[]
}

/* A refined `editEditor` input. `citationCheckDropped` is server-written and
 * present only on a call whose refinement dropped operations: `operations` then
 * holds the survivors, and each entry names the position a dropped op held in
 * the array the model sent — the positions the streaming paint used. */
export interface StageProposalsInput {
  editorId?: string
  operations: EditorOperation[]
  citationCheckDropped?: readonly CitationCheckDroppedEntry[]
  /* Server-written on a call staging onto a care plan under the fill-only
   * rule (see `fillOnlyRefusal`). Read from the call, never from a flag of the
   * browser's own, which can disagree with the server's for this request. */
  fillOnly?: boolean
  // Server-written beside `fillOnly`: the placeholder text it may replace.
  fillOnlyPlaceholders?: readonly string[]
}

interface RegistryApi {
  register: (id: string, entry: RegistryEntry) => () => void
  list: () => CollaborativeEditorAIMetadata[]
  readEditor: (id?: string) => ReadEditorResult | { error: string }
  stageProposals: (
    toolCallId: string,
    input: StageProposalsInput
  ) => StageProposalsResult
  /**
   * Paint decorations for a sub-batch of ops from an in-progress streaming
   * editEditor call. Only stages `operations[startIndex..]`, skips the FHIR
   * persistence path entirely — the full `stageProposals` call at
   * `tool-input-available` handles persistence as normal. Returns the number
   * of ops that newly staged (failures are silent; they will re-attempt or
   * fail with detail in the full `stageProposals` call).
   */
  stagePartialProposals: (
    toolCallId: string,
    input: StageProposalsInput,
    startIndex: number,
    endIndex?: number
  ) => number
  clearByToolCall: (toolCallId: string) => void
  /**
   * Supply the count of pending suggestions PERSISTED on the note for an editor
   * (the registry is FHIR-free, so the persisted state is fed in from
   * `useSuggestionPersistence`). `readEditor.hasPendingProposals` ORs this in so
   * the agent sees pending suggestions even before they hydrate into live
   * decorations (design Q5). Returns an unregister fn.
   */
  setPersistedPendingProvider: (
    id: string,
    provider: () => number
  ) => () => void
}

const CollaborativeEditorRegistryContext = createContext<RegistryApi | null>(
  null
)

export function CollaborativeEditorRegistryProvider({
  children,
}: {
  children: ReactNode
}): ReactNode {
  const entriesRef = useRef<Map<string, RegistryEntry>>(new Map())
  /* Per-editor "persisted pending count" providers, fed by
   * useSuggestionPersistence so readEditor can OR persisted-pending into
   * hasPendingProposals without the registry touching FHIR. */
  const pendingProvidersRef = useRef<Map<string, () => number>>(new Map())
  const noteSuggestionsEnabled = useFeatureFlag(featureFlags.aiNoteSuggestions)
  const citationMentionsEnabled = useFeatureFlag(
    featureFlags.aiCitationMentions
  )

  const register = useCallback((id: string, entry: RegistryEntry) => {
    entriesRef.current.set(id, entry)
    return () => {
      const current = entriesRef.current.get(id)
      if (current?.editor === entry.editor) {
        entriesRef.current.delete(id)
      }
    }
  }, [])

  const resolveEntry = useCallback(
    (id?: string): RegistryEntry | { error: string } => {
      const entries = entriesRef.current
      if (id) {
        const entry = entries.get(id)
        if (!entry) {
          return {
            error: `No editor registered with id "${id}". Active editors: ${
              Array.from(entries.keys()).join(', ') || '(none)'
            }`,
          }
        }
        return entry
      }
      if (entries.size === 0) {
        return { error: 'No editors are currently mounted on this page.' }
      }
      if (entries.size > 1) {
        return {
          error: `Multiple editors are mounted. Pass editorId. Options: ${Array.from(
            entries.keys()
          ).join(', ')}`,
        }
      }
      const onlyEntry = entries.values().next().value
      if (!onlyEntry) {
        return { error: 'No editors are currently mounted on this page.' }
      }
      return onlyEntry
    },
    []
  )

  const list = useCallback(
    () => Array.from(entriesRef.current.values()).map((e) => e.meta),
    []
  )

  const readEditor = useCallback(
    (id?: string): ReadEditorResult | { error: string } => {
      const entry = resolveEntry(id)
      if ('error' in entry) {
        return entry
      }
      const { editor, meta } = entry
      const html = editor.getHTML()
      const markdown = editorToMarkdown(editor)
      const fields = collectChoiceFields(editor)
      const taskItems = collectTaskItems(editor)
      const pluginState = AI_PROPOSALS_PLUGIN_KEY.getState(editor.state)
      const liveProposals = pluginState?.proposals.size ?? 0
      /* OR in persisted-pending: an edited resource can carry pending
       * suggestions that haven't hydrated into live decorations yet (fresh page
       * load, or a target that didn't resolve this mount). The agent must still
       * see them so its clear-and-regenerate ask fires (design Q5). */
      const persistedPending = pendingProvidersRef.current.get(meta.id)?.() ?? 0
      return {
        id: meta.id,
        meta,
        html,
        markdown,
        fields,
        taskItems,
        hasPendingProposals: liveProposals > 0 || persistedPending > 0,
      }
    },
    [resolveEntry]
  )

  const setPersistedPendingProvider = useCallback(
    (id: string, provider: () => number) => {
      pendingProvidersRef.current.set(id, provider)
      return () => {
        if (pendingProvidersRef.current.get(id) === provider) {
          pendingProvidersRef.current.delete(id)
        }
      }
    },
    []
  )

  const stageProposals = useCallback(
    (toolCallId: string, input: StageProposalsInput): StageProposalsResult => {
      const dropped = citationCheckDroppedOf(input)
      const droppedSummary = citationCheckDroppedSummary(dropped)
      const report = citationCheckDroppedReport(dropped)
      const withDrops = (summary: string) =>
        droppedSummary ? `${summary} ${droppedSummary}` : summary
      const entry = resolveEntry(input.editorId)
      if ('error' in entry) {
        return {
          ok: false,
          editorId: input.editorId ?? '(unknown)',
          staged: 0,
          failed: input.operations.map((op) => ({
            operation: op,
            reason: entry.error,
          })),
          summary: withDrops(entry.error),
          ...report,
          persisted: [],
        }
      }
      const { editor, meta } = entry
      if (meta.readOnly) {
        const msg = `Editor "${meta.id}" is read-only — no edits applied.`
        return {
          ok: false,
          editorId: meta.id,
          staged: 0,
          failed: input.operations.map((op) => ({
            operation: op,
            reason: msg,
          })),
          summary: withDrops(msg),
          ...report,
          persisted: [],
        }
      }

      /* A dropped op's streamed paint comes down first: nothing below will
       * overwrite its id, since survivors keep their own original positions,
       * and a ghost left there is a suggestion the clinician could accept
       * that the server refused. Decoration-only, the way hydration un-paints —
       * a streamed paint was never persisted. */
      for (const { index } of dropped) {
        editor.commands.rejectAIProposal(`${toolCallId}-${index}`)
      }
      const positions = survivingPositions(input.operations.length, dropped)

      const proposals: AIProposal[] = []
      /* Parallel to `proposals` so rollback can blame the right op AND we can
       * build the persistable record (id+op+label) for each staged proposal.
       * Failed resolutions skip pushing to `proposals`, which breaks index
       * alignment with `input.operations`. */
      const proposalOps: EditorOperation[] = []
      /* Revisions are staged one at a time rather than in the batch add: each
       * replaces a specific proposal, and that replace-and-delete has to be one
       * transaction so the clinician never sees the old card and the new one
       * together. Persistence is collected the same way as for the batch. */
      const revisions: {
        proposal: AIProposal
        op: EditorOperation
        supersededId: string
        // A displaced proposal, not a restated one: anchored at its own range.
        ownRange?: true
      }[] = []
      const failed: OperationFailure[] = []
      for (const [counter, inputOp] of input.operations.entries()) {
        const op = withFillOnlyStamp(input, inputOp)
        const proposalId = `${toolCallId}-${positions[counter]}`
        try {
          const selected =
            input.fillOnly === true
              ? fillOnlySelectedChoice(editor, op)
              : undefined
          if (selected !== undefined) {
            failed.push({ operation: op, reason: selected })
            editor.commands.rejectAIProposal(proposalId)
            continue
          }
          const resolved = resolveOperation(
            editor,
            op,
            noteSuggestionsEnabled,
            citationMentionsEnabled
          )
          if ('error' in resolved) {
            failed.push({ operation: op, reason: resolved.error })
            continue
          }
          const refusal =
            input.fillOnly === true
              ? fillOnlyRefusal(
                  editor,
                  op,
                  resolved,
                  fillOnlyPlaceholdersOf(input)
                )
              : undefined
          if (refusal !== undefined) {
            failed.push({ operation: op, reason: refusal })
            /* Its streamed paint comes down with it: the stream carried no
             * fill-only stamp, so the op may already be on screen as a ghost
             * the clinician could accept. A revision's id is new, so this
             * leaves the proposal it meant to replace exactly as it was. */
            editor.commands.rejectAIProposal(proposalId)
            continue
          }
          if (op.type === 'reviseProposal') {
            const persistedOp = revisedPersistedOp(editor, op)
            if (persistedOp === undefined) {
              failed.push({
                operation: op,
                reason:
                  'reviseProposal: the suggestion it names is no longer pending (the clinician accepted or rejected it meanwhile), so nothing was replaced.',
              })
              continue
            }
            revisions.push({
              proposal: toAIProposal(
                proposalId,
                toolCallId,
                resolved,
                undefined,
                undefined,
                persistedOp
              ),
              op,
              supersededId: op.supersedes,
            })
            continue
          }
          /* Under the fill-only rule a line holds one pending suggestion: a
           * proposal landing where an earlier one still waits replaces it, as
           * a revision would, rather than stacking a second on the line. */
          const overlapped =
            input.fillOnly === true
              ? overlappingPendingProposal(editor, resolved, toolCallId)
              : undefined
          if (overlapped !== undefined) {
            revisions.push({
              proposal: toAIProposal(
                proposalId,
                toolCallId,
                resolved,
                undefined,
                undefined,
                op
              ),
              op,
              supersededId: overlapped,
              ownRange: true,
            })
            continue
          }
          proposals.push(
            toAIProposal(
              proposalId,
              toolCallId,
              resolved,
              undefined,
              undefined,
              op
            )
          )
          proposalOps.push(op)
        } catch (err) {
          const reason =
            err instanceof Error ? err.message : `resolve threw: ${String(err)}`
          console.error('[stageProposals] resolve threw', { op, err })
          failed.push({ operation: op, reason })
        }
      }

      if (proposals.length > 0) {
        try {
          editor.commands.addAIProposals(proposals)
        } catch (err) {
          const reason =
            err instanceof Error
              ? err.message
              : `addAIProposals threw: ${String(err)}`
          console.error('[stageProposals] addAIProposals threw', {
            proposals,
            err,
          })
          /* Roll back: the proposals couldn't be staged, so the tool result
           * should report them as failed instead of leaving the chat in an
           * opaque "Edit failed" state with no diagnostics. */
          for (const op of proposalOps) {
            failed.push({ operation: op, reason })
          }
          proposals.length = 0
          proposalOps.length = 0
        }
      }

      /* Persistable records for the proposals that actually staged. Built from
       * the INPUT op (semantic, hydration-safe) keyed by the decoration id, so
       * a persisted record re-resolves to the same decoration on reload and
       * `clearByToolCall`/the reconciler line up. */
      const persisted: StagedProposalRecord[] = proposals.map((p, i) => ({
        id: p.id,
        op: proposalOps[i],
        label: p.label,
      }))

      let replaced = 0
      for (const { proposal, op, supersededId, ownRange } of revisions) {
        let superseded = false
        try {
          superseded = editor.commands.supersedeAIProposal(
            supersededId,
            proposal,
            { ownRange: ownRange === true }
          )
        } catch (err) {
          const reason =
            err instanceof Error
              ? err.message
              : `supersedeAIProposal threw: ${String(err)}`
          console.error('[stageProposals] supersedeAIProposal threw', {
            proposal,
            err,
          })
          failed.push({ operation: op, reason })
          continue
        }
        if (!superseded) {
          failed.push({
            operation: op,
            reason: `${op.type}: the earlier suggestion it was to replace is no longer pending (the clinician accepted or rejected it meanwhile), so this was not staged.`,
          })
          continue
        }
        proposals.push(proposal)
        replaced += 1
        persisted.push({
          id: proposal.id,
          /* `revisedPersistedOp` built it above and `toAIProposal` carried it
           * onto the proposal, which is the only place it survives to here. */
          op: proposal.op ?? op,
          ...(proposal.label !== undefined && { label: proposal.label }),
          supersedes: supersededId,
        })
      }

      if (proposals.length > 0) {
        entry.onProposalsStaged?.()
      }

      const summary = buildSummary(
        meta.label,
        proposals.length,
        failed.length,
        replaced
      )
      return {
        ok: failed.length === 0,
        editorId: meta.id,
        staged: proposals.length,
        failed,
        summary: withDrops(summary),
        ...report,
        persisted,
      }
    },
    [resolveEntry, noteSuggestionsEnabled, citationMentionsEnabled]
  )

  const stagePartialProposals = useCallback(
    (
      toolCallId: string,
      input: StageProposalsInput,
      startIndex: number,
      endIndex?: number
    ): number => {
      /* Resolve the editor; bail silently — full stageProposals will report the
       * error with detail once the input is available. */
      const entry = resolveEntry(input.editorId)
      if ('error' in entry) {
        return 0
      }
      const { editor, meta } = entry
      if (meta.readOnly) {
        return 0
      }

      const bound = endIndex ?? input.operations.length
      const ops = input.operations.slice(startIndex, bound)
      if (ops.length === 0) {
        return 0
      }

      /* Identity while the input streams, which carries no drops; the
       * original positions once a refined input with drops has landed, for the
       * reason `survivingPositions` gives. */
      const positions = survivingPositions(
        input.operations.length,
        citationCheckDroppedOf(input)
      )
      const proposals: AIProposal[] = []
      /* Index is relative to the FULL operations array so the proposal id
       * (`${toolCallId}-${idx}`) matches what the full stageProposals call will
       * create at tool-input-available, so the completing call OVERWRITES what the
       * stream painted rather than painting a duplicate beside it — the plugin's
       * add is a last-write-wins `Map.set` on the id, not a dedupe that refuses
       * the second write. An op whose repaint is NOT idempotent under that rule
       * therefore cannot stream; see the revision skip below. */
      for (let i = startIndex; i < bound; i++) {
        const inputOp = input.operations[i]
        if (!inputOp) {
          continue
        }
        const op = withFillOnlyStamp(input, inputOp)
        /* Revisions never paint from the stream. This path repaints the same
         * ids as the call completes, and a revision is not idempotent: the
         * first paint deletes the proposal it supersedes, so the completing
         * `stageProposals` would find nothing to replace and fail the op. The
         * clinician waits for the completed call instead of losing the edit. */
        if (op.type === 'reviseProposal') {
          continue
        }
        try {
          const resolved = resolveOperation(
            editor,
            op,
            noteSuggestionsEnabled,
            citationMentionsEnabled
          )
          if ('error' in resolved) {
            continue
          }
          /* A completed input can repaint here AFTER the full call refused
           * the op, and a repaint would put the refused ghost back. Only the
           * refined input carries the stamp, so a streaming paint still shows
           * and the completing call takes it down. */
          if (
            input.fillOnly === true &&
            (fillOnlySelectedChoice(editor, op) !== undefined ||
              fillOnlyRefusal(
                editor,
                op,
                resolved,
                fillOnlyPlaceholdersOf(input)
              ) !== undefined)
          ) {
            continue
          }
          proposals.push(
            toAIProposal(
              `${toolCallId}-${positions[i]}`,
              toolCallId,
              resolved,
              undefined,
              undefined,
              op
            )
          )
        } catch {
          /* Swallow — full stageProposals will surface detail */
        }
      }

      if (proposals.length === 0) {
        return 0
      }

      try {
        editor.commands.addAIProposals(proposals)
        /* Streaming paints before the full stageProposals call lands, so the
         * host has to reveal from here too — otherwise a deferred note shows the
         * mixed painted/hidden state for the length of the generation. */
        entry.onProposalsStaged?.()
        return proposals.length
      } catch {
        return 0
      }
    },
    [resolveEntry, noteSuggestionsEnabled, citationMentionsEnabled]
  )

  const clearByToolCall = useCallback((toolCallId: string) => {
    for (const { editor } of entriesRef.current.values()) {
      editor.commands.clearAIProposalsByToolCall(toolCallId)
    }
  }, [])

  const api = useMemo<RegistryApi>(
    () => ({
      register,
      list,
      readEditor,
      stageProposals,
      stagePartialProposals,
      clearByToolCall,
      setPersistedPendingProvider,
    }),
    [
      register,
      list,
      readEditor,
      stageProposals,
      stagePartialProposals,
      clearByToolCall,
      setPersistedPendingProvider,
    ]
  )

  return (
    <CollaborativeEditorRegistryContext.Provider value={api}>
      {children}
    </CollaborativeEditorRegistryContext.Provider>
  )
}

export function useCollaborativeEditorRegistry(): RegistryApi {
  const api = useContext(CollaborativeEditorRegistryContext)
  if (!api) {
    throw new Error(
      'useCollaborativeEditorRegistry must be used inside CollaborativeEditorRegistryProvider'
    )
  }
  return api
}

// Returns null instead of throwing when the provider isn't mounted (e.g., storybook).
export function useOptionalCollaborativeEditorRegistry(): RegistryApi | null {
  return useContext(CollaborativeEditorRegistryContext)
}

type ResolvedOperation = ProposalRenderSpec

/* A revision's persisted op: the superseded proposal's own op with its prose
 * swapped, and its source swapped for the one the revision cited — never the
 * original's, which backed other words.
 *
 * A `reviseProposal` op is never persisted as itself. It names a record that is
 * superseded the moment the revision stages, and `narrowPersistedOp` does not
 * know the type — so a verbatim persist would leave the revision unresolvable
 * on the next load, silently dropping the clinician's newest content. The
 * rewritten op re-resolves through the ordinary hydration path instead, which
 * needs to know nothing about revisions at all. */
function revisedPersistedOp(
  editor: Editor,
  op: Extract<EditorOperation, { type: 'reviseProposal' }>
): EditorOperation | undefined {
  const superseded = AI_PROPOSALS_PLUGIN_KEY.getState(
    editor.state
  )?.proposals.get(op.supersedes)
  if (!superseded?.op) {
    return undefined
  }
  const revised = reviseOpContent(superseded.op, op.content)
  if (!revised) {
    return undefined
  }
  const {
    citation: _citation,
    sourceProvenance: _sourceProvenance,
    source: _source,
    ...uncited
  } = revised as EditorOperation & {
    citation?: unknown
    sourceProvenance?: unknown
    source?: unknown
  }
  return { ...uncited, ...citationOf(op) } as EditorOperation
}

/* An op's own content is AI-authored, so its mentions carry the author's label
 * (withAuthoredMentionLabels) — the resolved htmlContent is what the preview,
 * the island seed, and the accept's insert all parse, so marking it here marks
 * it once for all three. The two branches that build their HTML from a node
 * ALREADY in the document (setChoiceById, setTaskItemChecked) are deliberately
 * not marked. Neither emits a mention today — each rebuilds a single field or
 * list item — but the mentions in a clinician's content are the clinician's,
 * and a label marked onto one would freeze a chip that otherwise re-derives its
 * text from the live resource on every render. A new op branch inherits the
 * distinction: mark what the model wrote, never what the document already
 * held. */
export function resolveOperation(
  editor: Editor,
  op: EditorOperation,
  // The ai-note-suggestions gate (required — see
  // mentionContractFor). Threaded to every validateSuggestionContent /
  // contractForNode call below so a missed call site fails to compile.
  noteSuggestionsEnabled: boolean,
  // The ai-citation-mentions gate (required — see mentionContractFor). Threaded
  // alongside noteSuggestionsEnabled to every validateSuggestionContent /
  // contractForNode call below so a missed call site fails to compile.
  citationMentionsEnabled: boolean
): ResolvedOperation | { error: string } {
  switch (op.type) {
    case 'replaceText': {
      const guard =
        citationIssue('replaceText', op) ??
        validateSuggestionContent(
          editor,
          'replaceText',
          op.newContent,
          noteSuggestionsEnabled,
          citationMentionsEnabled
        )
      if (guard) {
        return guard
      }
      const match = findUniqueTextRange(editor, op.findText)
      if ('error' in match) {
        return {
          error: `replaceText: ${describeAnchorFailure(editor, op.findText, match)}`,
        }
      }
      const trimmed: { html: string } | { error: string } = match.segmentContext
        ? trimReSuppliedContext(editor, op.newContent, match.segmentContext)
        : { html: op.newContent }
      if ('error' in trimmed) {
        return { error: `replaceText: ${trimmed.error}` }
      }
      const content = stagedFillOnly(op)
        ? {
            html: keepingLabelColon(
              editor.state.doc,
              match.from,
              match.to,
              trimmed.html
            ),
          }
        : trimmed
      /* A fill-only replace that restates a question word for word and only
       * adds its answer is an insert of that answer, so it goes where an
       * insert after the question would: into the empty bullet the template
       * left for it, if there is one. */
      const slot = stagedFillOnly(op)
        ? emptyAnswerSlotAfter(editor.state.doc, match.to)
        : undefined
      const answer =
        slot === undefined
          ? null
          : appendedHtml(editor.state.doc, match.from, match.to, content.html)
      if (slot !== undefined && answer !== null) {
        return {
          ...slotPlacement(editor.state.doc, slot, answer),
          htmlContent: withAuthoredMentionLabels(answer),
          label: `Insert after "${truncate(op.findText)}"`,
          render: { type: 'default' },
          ...citationOf(op),
          fillOnly: true,
        }
      }
      return {
        kind: 'replace',
        from: match.from,
        to: match.to,
        htmlContent: withAuthoredMentionLabels(content.html),
        label: `Replace "${truncate(op.findText)}"`,
        render: { type: 'default' },
        ...citationOf(op),
        ...(stagedFillOnly(op) && { fillOnly: true as const }),
      }
    }
    case 'replaceNode': {
      const anchor = findUniqueTextRange(editor, op.anchorText)
      if ('error' in anchor) {
        return {
          error: `replaceNode: ${describeAnchorFailure(editor, op.anchorText, anchor)}`,
        }
      }
      const target = findFieldNodeAfter(editor, anchor.to)
      if ('error' in target) {
        return { error: `replaceNode: ${target.error}` }
      }
      const contentTypes = collectNodeTypes(editor, op.newContent)
      const fieldTypes = contentTypes.filter(
        (type) => !SIMPLE_HTML_NODE_TYPES.has(type)
      )
      if (fieldTypes.length === 0) {
        return {
          error:
            'replaceNode: newContent has no interactive field — supply the field\'s HTML (e.g. a <button data-type="choice" …>), or use replaceText for plain text.',
        }
      }
      /* Require exactly one field of the target type. Allowing several would
       * replace one node with many — the duplication this operation exists to
       * prevent. */
      if (fieldTypes.length > 1 || fieldTypes[0] !== target.typeName) {
        return {
          error: `replaceNode: the field after this anchor is "${target.typeName}", but newContent parses to "${fieldTypes.join(', ')}". Replace it with a single field of the same type.`,
        }
      }
      /* The field replaces an inline atom in place. Any sibling content (stray
       * text, a heading, a list) would be block/inline material handed to
       * insertContentAt at an inline-atom position, where the schema silently
       * drops or floats it. Only the wrapper nodes generateJSON injects around
       * bare field HTML are allowed alongside the field. */
      const hasStrayContent = contentTypes.some(
        (type) =>
          type !== target.typeName && !REPLACE_NODE_WRAPPER_TYPES.has(type)
      )
      if (hasStrayContent) {
        return {
          error:
            'replaceNode: newContent must contain only the replacement field, nothing else — send just the field\'s HTML (e.g. <button data-type="choice" …>).',
        }
      }
      /* The replacement field's serialized attrs must satisfy its contract
       * (permissive pass-through today for replace-target families; tightened
       * per family in Phase 3). The type-match above guarantees exactly one
       * custom node here. */
      const [replacement] = collectCustomNodes(
        editor,
        op.newContent
      ).customNodes
      if (replacement) {
        const contract = contractForNode(
          replacement.type,
          replacement.attrs,
          noteSuggestionsEnabled,
          citationMentionsEnabled
        )
        const issue = contract && attrsIssue(contract, replacement.attrs)
        if (issue) {
          return {
            error: `replaceNode: ${replacement.type} attrs invalid: ${issue}`,
          }
        }
      }
      return {
        kind: 'replace',
        from: target.from,
        to: target.to,
        htmlContent: withAuthoredMentionLabels(op.newContent),
        label: `Update field after "${truncate(op.anchorText)}"`,
        render: { type: 'default' },
        ...citationOf(op),
      }
    }
    case 'setChoiceById': {
      const target = findChoiceNodeByFieldId(editor, op.fieldId)
      if (!target) {
        return {
          error: `setChoiceById: no choice field with fieldId "${op.fieldId}" — re-read the editor to get a current fieldId from the fields list.`,
        }
      }
      const options = parseChoiceOptions(target.node.attrs.options)
      if (!options.includes(op.value)) {
        return {
          error: `setChoiceById: value "${truncate(op.value)}" is not one of the field's options (${options.join(', ') || 'none'}). Copy a value exactly from the field's options.`,
        }
      }
      /* A selected field can't render the inline option highlight, and a choice
       * op must never fall back to the default insert preview — so the op is
       * unplaceable until the selection is cleared. Hydration keeps the record
       * pending and counts it into "could not be placed"; the live staging path
       * reports the failure to the agent. */
      if (target.node.attrs.display) {
        return {
          error: `setChoiceById: the field already has a selection ("${truncate(String(target.node.attrs.display))}"). Re-read the editor — to change an answered field, use replaceNode.`,
        }
      }
      return {
        kind: 'replace',
        from: target.from,
        to: target.to,
        htmlContent: buildChoiceButtonHtml(target.node, op.value),
        label: `Set choice to "${truncate(op.value)}"`,
        render: { type: 'choiceOption', value: op.value },
        /* Carried onto the proposal and persisted with it. The pick paints an
         * inline option highlight rather than a card, and its source is drawn
         * under the line holding the field (`choiceSourceProposal`). */
        ...citationOf(op),
      }
    }
    case 'insertAfterText': {
      const guard =
        citationIssue('insertAfterText', op) ??
        validateSuggestionContent(
          editor,
          'insertAfterText',
          op.content,
          noteSuggestionsEnabled,
          citationMentionsEnabled
        )
      if (guard) {
        return guard
      }
      const match = findUniqueTextRange(editor, op.anchorText)
      if ('error' in match) {
        return {
          error: `insertAfterText: ${describeAnchorFailure(editor, op.anchorText, match)}`,
        }
      }
      /* Only under the fill-only rule does an answer go into the empty bullet
       * a template leaves under its question; otherwise it lands right after
       * the anchor, as on main. */
      const slot = stagedFillOnly(op)
        ? emptyAnswerSlotAfter(editor.state.doc, match.to)
        : undefined
      const placed =
        slot === undefined
          ? { kind: 'insert' as const, from: match.to, to: match.to }
          : slotPlacement(editor.state.doc, slot, op.content)
      return {
        ...placed,
        htmlContent: withAuthoredMentionLabels(op.content),
        label: `Insert after "${truncate(op.anchorText)}"`,
        render: { type: 'default' },
        ...citationOf(op),
      }
    }
    case 'insertBeforeText': {
      const guard =
        citationIssue('insertBeforeText', op) ??
        validateSuggestionContent(
          editor,
          'insertBeforeText',
          op.content,
          noteSuggestionsEnabled,
          citationMentionsEnabled
        )
      if (guard) {
        return guard
      }
      const match = findUniqueTextRange(editor, op.anchorText)
      if ('error' in match) {
        return {
          error: `insertBeforeText: ${describeAnchorFailure(editor, op.anchorText, match)}`,
        }
      }
      return {
        kind: 'insert',
        from: match.from,
        to: match.from,
        htmlContent: withAuthoredMentionLabels(op.content),
        label: `Insert before "${truncate(op.anchorText)}"`,
        render: { type: 'default' },
        ...citationOf(op),
      }
    }
    case 'appendToSection': {
      const guard =
        citationIssue('appendToSection', op) ??
        validateSuggestionContent(
          editor,
          'appendToSection',
          op.content,
          noteSuggestionsEnabled,
          citationMentionsEnabled
        )
      if (guard) {
        return guard
      }
      const pos = findSectionEnd(editor, op.headingText)
      if ('error' in pos) {
        return { error: `appendToSection: ${pos.error}` }
      }
      return {
        kind: 'insert',
        from: pos.position,
        to: pos.position,
        htmlContent: withAuthoredMentionLabels(op.content),
        label: `Append to section "${truncate(op.headingText)}"`,
        render: { type: 'default' },
        ...citationOf(op),
      }
    }
    case 'insertAtEnd': {
      const guard =
        citationIssue('insertAtEnd', op) ??
        validateSuggestionContent(
          editor,
          'insertAtEnd',
          op.content,
          noteSuggestionsEnabled,
          citationMentionsEnabled
        )
      if (guard) {
        return guard
      }
      const end = editor.state.doc.content.size
      return {
        kind: 'insert',
        from: end,
        to: end,
        htmlContent: withAuthoredMentionLabels(op.content),
        label: 'Append to end of document',
        render: { type: 'default' },
        ...citationOf(op),
      }
    }
    case 'replaceAll': {
      const guard =
        citationIssue('replaceAll', op) ??
        validateSuggestionContent(
          editor,
          'replaceAll',
          op.content,
          noteSuggestionsEnabled,
          citationMentionsEnabled
        )
      if (guard) {
        return guard
      }
      return {
        kind: 'replace',
        from: 0,
        to: editor.state.doc.content.size,
        htmlContent: withAuthoredMentionLabels(op.content),
        label: 'Replace entire document',
        render: { type: 'default' },
        ...citationOf(op),
      }
    }
    case 'setTaskItemChecked': {
      const resolved = resolveSetTaskItemChecked(
        editor,
        op.sectionHeading ?? null,
        op.labelText,
        op.checked
      )
      return 'error' in resolved ? resolved : { ...resolved, ...citationOf(op) }
    }
    /* A revision resolves from the proposal it replaces, not from the document:
     * it inherits that proposal's anchor, kind and label, and changes only the
     * content. Re-deriving the position from the new text would text-match
     * against a document the original was never accepted into, and land
     * somewhere else or nowhere.
     *
     * It carries no citation of its own — the schema has no field for one — so
     * `citationOf` reads what the SERVER wrote onto the op when it carried the
     * original's source forward. */
    case 'reviseProposal': {
      const superseded = AI_PROPOSALS_PLUGIN_KEY.getState(
        editor.state
      )?.proposals.get(op.supersedes)
      if (!superseded) {
        return {
          error:
            'reviseProposal: the id it names is not a proposal awaiting a decision in this editor — the clinician has already accepted or rejected it, or it was never staged here. Nothing was replaced; propose the change as a new operation if it still applies.',
        }
      }
      if (
        superseded.op === undefined ||
        revisableContentField(superseded.op) === undefined
      ) {
        return {
          error:
            'reviseProposal: the proposal it names holds no prose to restate. Propose the change as an ordinary operation instead.',
        }
      }
      const guard = validateSuggestionContent(
        editor,
        'reviseProposal',
        op.content,
        noteSuggestionsEnabled,
        citationMentionsEnabled
      )
      if (guard) {
        return guard
      }
      return {
        kind: superseded.kind,
        from: superseded.from,
        to: superseded.to,
        htmlContent: withAuthoredMentionLabels(op.content),
        ...(superseded.label !== undefined && { label: superseded.label }),
        render: superseded.render,
        ...citationOf(op),
        // A revision restates its original, so it paints under the same rule.
        ...(superseded.fillOnly && { fillOnly: true as const }),
      }
    }
  }
}

/* The care plan's fill-only rule, the half only the live document can decide.
 *
 * The server refuses every op that removes or changes content by its shape
 * alone, and a single-line `replaceText` by its own text. What it cannot see is
 * which line a multi-line anchor lands on — the one the document repeats — or,
 * for a revision, what range the original proposal covers. Both are known
 * here, after resolution: the text the op would replace is exactly the range
 * it resolved to, and what goes in its place is exactly the content it
 * resolved to (context lines already trimmed off). The same shared rule the
 * server applies then says whether anything but placeholder text would go. */
export function fillOnlyRefusal(
  editor: Editor,
  op: EditorOperation,
  resolved: ResolvedOperation,
  // The template placeholder text the server let this call replace.
  placeholders: readonly string[]
): string | undefined {
  if (
    resolved.kind !== 'replace' ||
    (op.type !== 'replaceText' && op.type !== 'reviseProposal')
  ) {
    return undefined
  }
  const matched = editor.state.doc.textBetween(resolved.from, resolved.to, '\n')
  /* A field or chip inside the range is invisible to the text comparison — an
   * inline atom renders no text, so an anchor can match straight across one —
   * yet replacing the range deletes it: a selected choice, a mention, a
   * citation chip. None of those is placeholder text, so any of them refuses
   * the op. A hard break is only a line break. */
  const fields = inlineFieldsBetween(editor, resolved.from, resolved.to)
  if (fields.length > 0) {
    return `${op.type}: fill-only — the text this matched, "${truncate(matched, 80)}", has ${fields.length === 1 ? `a ${fields[0]} field` : `${fields.length} fields (${fields.join(', ')})`} inside it, and replacing it would delete ${fields.length === 1 ? 'that field' : 'them'}. Leave existing content alone: anchor on text that does not span a field, and pick an empty choice with setChoiceById.`
  }
  const replacement = htmlVisibleText(resolved.htmlContent)
  const removed = removedNonPlaceholderText(matched, replacement, placeholders)
  if (removed.length === 0 && !isBlankFillText(replacement)) {
    return undefined
  }
  const prefix = `${op.type}: fill-only — the text this matched, "${truncate(matched, 80)}",`
  if (removed.length === 0) {
    return `${prefix} would be replaced with nothing. Leave existing content alone: fill the placeholder with its value, or leave it as it is.`
  }
  /* Never calls placeholder text "not placeholder": a fragment of one — an
   * anchor that cut a placeholder short — is named as the fragment it is. */
  const described = removed.map((piece) => {
    const placeholder = placeholderContaining(piece, placeholders)
    return placeholder === undefined
      ? `"${piece}", which is existing wording, not template placeholder text`
      : `"${piece}", only part of the placeholder "${placeholder}" — anchor the whole placeholder`
  })
  return `${prefix} includes ${described.join('; and ')}, and this edit would remove it. Leave existing content alone: keep existing wording word for word and fill only the placeholder, or add your text next to it with insertAfterText.`
}

/* The inline nodes other than text and line breaks inside a range, by type:
 * the fields and chips a replacement of that range would delete. */
function inlineFieldsBetween(
  editor: Editor,
  from: number,
  to: number
): string[] {
  const fields: string[] = []
  editor.state.doc.nodesBetween(from, to, (node) => {
    if (node.isInline && !node.isText && node.type.name !== HARD_BREAK_NODE) {
      fields.push(node.type.name)
    }
    return true
  })
  return fields
}

/* The placeholder list the server stamped on the call: the care plan's
 * catalog, or none on a visit note. A stamped call that somehow carries no list
 * gets none — the rule fails closed, allowing only what keeps every word. */
/* Inside the empty bullet a template leaves under a question for its answer,
 * when `pos` ends the question's line and that bullet is the whole of the list
 * right after it. The model can only anchor on text, and the slot has none, so
 * an answer it inserts after the question goes there — in the preview and on
 * accept, which resolve the same way — rather than between the question and
 * its slot, which would leave an empty bullet under the answer. A list holding
 * anything, or one that does not directly follow the line, is left alone. */
function emptyAnswerSlotAfter(doc: PMNode, pos: number): number | undefined {
  const $pos = doc.resolve(pos)
  if (!$pos.parent.isTextblock || pos !== $pos.end()) {
    return undefined
  }
  const after = $pos.after()
  const list = doc.resolve(after).nodeAfter
  const item = list?.childCount === 1 ? list.firstChild : null
  const line = item?.childCount === 1 ? item.firstChild : null
  if (
    !list ||
    !EMPTY_SLOT_LISTS.has(list.type.name) ||
    !line?.isTextblock ||
    line.content.size > 0
  ) {
    return undefined
  }
  // Into the list, its one item, and that item's empty line.
  return after + 3
}

const EMPTY_SLOT_LISTS = new Set(['bulletList', 'orderedList'])

interface SlotPlacementResult {
  kind: 'insert' | 'replace'
  from: number
  to: number
}

/* Where an answer goes in the empty bullet at `slot`. An answer that is itself
 * one list takes the empty list's place, so its items are the slot's items
 * rather than a sub-list under an empty bullet; anything else goes into the
 * empty bullet's line. */
function slotPlacement(
  doc: PMNode,
  slot: number,
  html: string
): SlotPlacementResult {
  if (!isOneList(doc.type.schema, html, EMPTY_SLOT_LISTS)) {
    return { kind: 'insert', from: slot, to: slot }
  }
  // The slot's line sits in the list's one item, two levels in.
  const $slot = doc.resolve(slot)
  const depth = $slot.depth - 2
  return { kind: 'replace', from: $slot.before(depth), to: $slot.after(depth) }
}

/* The pending proposal a new one would stack on, so that it replaces it
 * instead: the most recent one on this record whose range meets the new one's,
 * or that sits on a line the new one also touches. A line is one textblock's
 * stretch between hard breaks, so the scaffold's own lines stay apart. Proposals
 * from the same call never replace each other, and choice picks — a node, not
 * a line — are left alone. */
function overlappingPendingProposal(
  editor: Editor,
  resolved: ProposalRenderSpec,
  toolCallId: string
): string | undefined {
  if (resolved.render.type === 'choiceOption') {
    return undefined
  }
  const { doc } = editor.state
  const lines = new Set(
    [resolved.from, resolved.to].flatMap((pos) => lineOf(doc, pos) ?? [])
  )
  let overlapped: string | undefined
  for (const [id, pending] of AI_PROPOSALS_PLUGIN_KEY.getState(editor.state)
    ?.proposals ?? []) {
    if (
      pending.toolCallId === toolCallId ||
      pending.render.type === 'choiceOption'
    ) {
      continue
    }
    const intersects = pending.from < resolved.to && resolved.from < pending.to
    const sharesLine = [pending.from, pending.to].some((pos) => {
      const line = lineOf(doc, Math.min(pos, doc.content.size))
      return line !== undefined && lines.has(line)
    })
    if (intersects || sharesLine) {
      overlapped = id
    }
  }
  return overlapped
}

// `<textblock start>:<line>`, or nothing for a position outside a textblock.
function lineOf(doc: PMNode, pos: number): string | undefined {
  const $pos = doc.resolve(pos)
  if (!$pos.parent.isTextblock) {
    return undefined
  }
  let line = 0
  $pos.parent.forEach((child, offset) => {
    if (offset < $pos.parentOffset && child.type.name === 'hardBreak') {
      line += 1
    }
  })
  return `${$pos.start()}:${line}`
}

/* A replace or insert-after staged under the fill-only rule carries the rule on
 * its op, so the persisted record does too and a reload paints it the same way:
 * the stamp is on the call, which a reload never sees. Only those two read it. */
function withFillOnlyStamp(
  input: StageProposalsInput,
  op: EditorOperation
): EditorOperation {
  if (
    input.fillOnly !== true ||
    (op.type !== 'replaceText' && op.type !== 'insertAfterText')
  ) {
    return op
  }
  const stamped: EditorOperation & { fillOnly: true } = {
    ...op,
    fillOnly: true,
  }
  return stamped
}

// Read structurally: the stamp is not part of the model-facing op schema.
function stagedFillOnly(op: EditorOperation): boolean {
  return (op as { fillOnly?: unknown }).fillOnly === true
}

function fillOnlyPlaceholdersOf(input: StageProposalsInput): readonly string[] {
  return input.fillOnlyPlaceholders ?? []
}

/* Under the fill-only rule a choice that already has a selection stays as it
 * is. `resolveOperation` refuses it too, but its advice — change it with
 * replaceNode — is advice the server refuses under this rule. */
function fillOnlySelectedChoice(
  editor: Editor,
  op: EditorOperation
): string | undefined {
  if (op.type !== 'setChoiceById') {
    return undefined
  }
  const display = findChoiceNodeByFieldId(editor, op.fieldId)?.node.attrs
    .display
  return display
    ? `setChoiceById: the field already has a selection ("${truncate(String(display))}"). It stays as it is — leave existing content alone; pick a value only for a field with no selection.`
    : undefined
}

// The visible text of an HTML fragment, entities decoded.
function htmlVisibleText(html: string): string {
  return (
    new DOMParser().parseFromString(html, 'text/html').body.textContent ?? ''
  )
}

/* Standard rich-text node types — the ones a text operation may safely
 * introduce. Anything outside this set is a custom field node (choice dropdown,
 * mention, observation, validated tool, …). Re-parsing such a node's serialized
 * HTML re-instantiates it, so letting a text operation insert that markup
 * duplicates the field rather than editing it — the corruption replaceNode and
 * this guard exist to prevent. Listing standard nodes (rather than the open-
 * ended set of field nodes) keeps every current and future field type blocked
 * while still allowing ordinary formatting. */
const HARD_BREAK_NODE = 'hardBreak'

const SIMPLE_HTML_NODE_TYPES = new Set([
  'doc',
  'paragraph',
  'text',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'taskList',
  'taskItem',
  'blockquote',
  'codeBlock',
  'horizontalRule',
  HARD_BREAK_NODE,
  'image',
])

/* The structural nodes generateJSON wraps around bare inline field HTML
 * (a `<button>` becomes doc > paragraph > field). Allowed alongside the field
 * in a replaceNode replacement; anything else is stray content. */
const REPLACE_NODE_WRAPPER_TYPES = new Set(['doc', 'paragraph'])

function collectNodeTypes(editor: Editor, html: string): string[] {
  const types: string[] = []
  const visit = (node: JSONContent): void => {
    if (node.type) {
      types.push(node.type)
    }
    node.content?.forEach(visit)
  }
  visit(generateJSON(html, editor.extensionManager.extensions) as JSONContent)
  return types
}

interface CustomNode {
  type: string
  attrs: JsonObject
}

interface GateWalk {
  customNodes: CustomNode[]
  fieldIds: string[]
  restorationIds: string[]
}

/* Walk an op's parsed HTML, collecting every custom field node (any type outside
 * SIMPLE_HTML_NODE_TYPES) with its attrs, plus the durable ids whose duplication
 * would corrupt the doc. Attrs round-trip identically through data-* HTML and PM
 * JSON, so the parsed JSON is the contract's input either way. */
function collectCustomNodes(editor: Editor, html: string): GateWalk {
  const walk: GateWalk = { customNodes: [], fieldIds: [], restorationIds: [] }
  const visit = (node: JSONContent): void => {
    const type = node.type
    if (type && type !== 'doc' && !SIMPLE_HTML_NODE_TYPES.has(type)) {
      const attrs: JsonObject = node.attrs ?? {}
      walk.customNodes.push({ type, attrs })
      if (type === 'choice' && typeof attrs.fieldId === 'string') {
        walk.fieldIds.push(attrs.fieldId)
      }
      if (
        type === 'validated-tool' &&
        typeof attrs.restorationId === 'string'
      ) {
        walk.restorationIds.push(attrs.restorationId)
      }
    }
    node.content?.forEach(visit)
  }
  visit(generateJSON(html, editor.extensionManager.extensions) as JSONContent)
  return walk
}

/* Raw-HTML scan for data-type tokens naming a node type the schema doesn't
 * define. generateJSON silently strips such nodes, so without this an
 * AI-fabricated node type vanishes rather than surfacing a reason. data-type is
 * exclusively the custom-node discriminator in serialized content, so a token
 * that isn't a known node name is genuinely fabricated. */
function unknownDataTypes(editor: Editor, html: string): string[] {
  const known = editor.schema.nodes
  const out = new Set<string>()
  for (const match of html.matchAll(/data-type="([^"]+)"/g)) {
    const type = match[1]
    if (known[type] === undefined) {
      out.add(type)
    }
  }
  return [...out]
}

function firstDuplicate(values: string[]): string | null {
  const seen = new Set<string>()
  for (const value of values) {
    if (seen.has(value)) {
      return value
    }
    seen.add(value)
  }
  return null
}

/* safeParse a custom node's attrs against its contract, returning the first
 * issue as a human-readable string, or null when valid. */
function attrsIssue(
  contract: NonNullable<ReturnType<typeof contractForNode>>,
  attrs: JsonObject
): string | null {
  const parsed = contract.attrs.safeParse(attrs)
  if (parsed.success) {
    return null
  }
  const issue = parsed.error.issues[0]
  return `${issue.path.join('.') || '(root)'} — ${issue.message}`
}

/* The AI validation gate (design 1.1). Subsumes the blanket SIMPLE_HTML_NODE_TYPES
 * ban into a per-node insertPolicy: content is parsed through the real editor
 * schema, then every custom field node is checked against its contract — its
 * insertPolicy for this text operation, then its attrs schema — and durable-id
 * duplication is rejected. Returns an error to drop the op with a specific,
 * human-readable reason, or null when the content is admissible. A text op admits
 * a custom node only when its policy is 'insertable' (today: condition mentions);
 * everything else is dropped, as before, but with a per-node reason. Placement
 * against the resolved target is checked downstream by the per-op resolution,
 * unchanged. */
export function validateSuggestionContent(
  editor: Editor,
  opType: string,
  html: string,
  noteSuggestionsEnabled: boolean,
  citationMentionsEnabled: boolean
): { error: string } | null {
  const unknown = unknownDataTypes(editor, html)
  if (unknown.length > 0) {
    return {
      error: `${opType} references node type(s) the editor does not define: ${unknown.join(', ')}. Use only standard formatting tags.`,
    }
  }
  const { customNodes, fieldIds, restorationIds } = collectCustomNodes(
    editor,
    html
  )
  const dupField = firstDuplicate(fieldIds)
  if (dupField) {
    return {
      error: `${opType}: duplicate choice fieldId "${dupField}" — each field id must be unique.`,
    }
  }
  const dupRestoration = firstDuplicate(restorationIds)
  if (dupRestoration) {
    return {
      error: `${opType}: duplicate validated-tool restorationId "${dupRestoration}".`,
    }
  }
  for (const node of customNodes) {
    const contract = contractForNode(
      node.type,
      node.attrs,
      noteSuggestionsEnabled,
      citationMentionsEnabled
    )
    const policy = contract?.insertPolicy ?? 'never'
    const label =
      contract && contract.subtype !== node.type
        ? `${node.type}:${contract.subtype}`
        : node.type
    if (policy !== 'insertable') {
      return {
        error: `${opType} may not introduce a "${label}" field (insertPolicy=${policy}). To change an interactive field, use replaceNode; for plain text, use replaceText.`,
      }
    }
    // Insert-admission only: a version-pinned condition reference reads as
    // already-completed to the accept pipeline (a versioned reference is the
    // linkage-done marker), so an inserted versioned mention would silently skip
    // linkage yet sign clean. Reject it here with a self-correcting reason. The
    // shared conditionMentionAttrs schema stays permissive, so a clinician's
    // versioned mention swapped via replaceNode still validates (main parity).
    if (
      contract?.subtype === 'condition' &&
      typeof node.attrs.reference === 'string' &&
      isVersionedReferenceString(node.attrs.reference)
    ) {
      return {
        error: `${opType} may not introduce a version-pinned condition mention — reference the unversioned "Condition/<id>" so its linkage completes on accept.`,
      }
    }
    const issue = contract && attrsIssue(contract, node.attrs)
    if (issue) {
      return { error: `${opType}: ${label} attrs invalid: ${issue}` }
    }
  }
  return null
}

interface FieldNode {
  from: number
  to: number
  typeName: string
}

// Find the first interactive field (inline atom node — choice dropdown, mention,
// …) at or after `anchorTo`, scoped to the textblock the anchor sits in so we
// only match a field on the same line as its label.
export function findFieldNodeAfter(
  editor: Editor,
  anchorTo: number
): FieldNode | { error: string } {
  const $pos = editor.state.doc.resolve(anchorTo)
  let depth = $pos.depth
  while (depth > 0 && !$pos.node(depth).isTextblock) {
    depth -= 1
  }
  const block = $pos.node(depth)
  let cursor = $pos.start(depth)
  let found: FieldNode | null = null
  block.forEach((child) => {
    const childFrom = cursor
    cursor += child.nodeSize
    if (found || child.isText || !child.type.isAtom || childFrom < anchorTo) {
      return
    }
    found = {
      from: childFrom,
      to: childFrom + child.nodeSize,
      typeName: child.type.name,
    }
  })
  if (!found) {
    return {
      error:
        'no interactive field found at or after the anchor on the same line — point anchorText at the label immediately before the field',
    }
  }
  return found
}

interface TextRange {
  from: number
  to: number
  /* Set only by the segmented pass, which covers one line of a multi-line
   * anchor and treats the rest as context (see `resolveSegmentedAnchor`). Those
   * context lines travel with the range so `replaceText` can recognise a
   * `newContent` that re-supplies them and trim them back off. Absent on every
   * other pass, where the range covers exactly what the anchor named. */
  segmentContext?: SegmentedAnchorContext
}

// One line of a multi-line anchor: the normalized form the matcher compares,
// and the line as the model wrote it, which is what a failure quotes back.
interface AnchorSegment {
  normalized: string
  display: string
}

// The lines of a multi-line anchor either side of the one it resolved to.
interface SegmentedAnchorContext {
  before: AnchorSegment[]
  after: AnchorSegment[]
}

/* Why an anchor didn't resolve, as a code rather than a message. The `error`
 * string beside it is the model's retry instruction and is free to be reworded;
 * this field is the contract the clinician-facing copy branches on, so the two
 * failures can be described differently without anyone parsing prose. An empty
 * needle reports `notFound` — there was nothing to look for — and keeps its own
 * message for the model. */
export interface AnchorFailure {
  error: string
  reason: AnchorFailureKind
}

// Find a text range in the document. Returns an error if absent or ambiguous.
//
// Four passes, each tried only when every earlier one found nothing, so a more
// literal reading of the anchor always wins:
//
//   1. exact `indexOf` within a textblock
//   2. the same search over a normalized form of BOTH sides (`normalizeForMatch`)
//   3. pass 2 again with the anchor's HTML markup stripped off it
//   4. `resolveSegmentedAnchor`, for an anchor naming several lines
//
// Anchor text is authored by a model against note content a clinician may since
// have edited, so a pasted smart quote or a re-wrapped line should not defeat
// placement. An ambiguous result stays an error in every pass rather than a
// guess, and every pass returns a range inside a single textblock.
//
// Passes 1-3 preserve the needle's extent: normalization folds characters but
// never drops any, so a match always covers what the anchor asked for. An anchor
// the note cannot supply verbatim — a model-added trailing period, say — is a
// failed op with a reason, which the tool schema already tells the model to
// retry, rather than a silent match over less text than it named. Pass 4 is the
// one deliberate exception, and covers less than the anchor named because a
// multi-line anchor IS partly context; see `resolveSegmentedAnchor` for the
// conditions that make which part unambiguous.
export function findUniqueTextRange(
  editor: Editor,
  needle: string
): TextRange | AnchorFailure {
  if (!needle) {
    return { error: 'anchor text is empty', reason: 'notFound' }
  }
  const ambiguous: AnchorFailure = {
    error: `text "${truncate(needle)}" matches multiple locations — use a longer or more specific anchor`,
    reason: 'ambiguous',
  }
  const exact = collectTextblockMatches(editor, (block, blockPos, out) => {
    findInTextblock(block, blockPos, needle, out)
  })
  if (exact.length === 1) {
    return exact[0]
  }
  if (exact.length > 1) {
    return ambiguous
  }
  const normalized = normalizeForMatch(needle).text
  if (normalized) {
    const relaxed = collectTextblockMatches(editor, (block, blockPos, out) => {
      findNormalizedInTextblock(block, blockPos, normalized, out)
    })
    if (relaxed.length === 1) {
      return relaxed[0]
    }
    if (relaxed.length > 1) {
      return ambiguous
    }
  }
  const sanitized = sanitizeAnchorNeedle(needle)
  // Third pass: the same normalized search over the anchor with its markup
  // stripped, for the anchor whose visible text all sits in one textblock (an
  // inline <strong> run copied mid-sentence). Positions come from the existing
  // per-block mapping, so this pass adds no new offset arithmetic.
  if (sanitized.text !== needle) {
    const plain = normalizeForMatch(sanitized.text).text
    if (plain && plain !== normalized) {
      const stripped = collectTextblockMatches(
        editor,
        (block, blockPos, out) => {
          findNormalizedInTextblock(block, blockPos, plain, out)
        }
      )
      if (stripped.length === 1) {
        return stripped[0]
      }
      if (stripped.length > 1) {
        return ambiguous
      }
    }
  }
  // Fourth pass: the anchor names several lines, so it is context plus a target
  // rather than one span (see `resolveSegmentedAnchor`).
  if (sanitized.segments.length > 1) {
    const segmented = resolveSegmentedAnchor(editor, sanitized)
    if (segmented === 'ambiguous') {
      return ambiguous
    }
    if (segmented !== 'unresolved') {
      return segmented
    }
  }
  return {
    error: `text "${truncate(needle)}" not found in document`,
    reason: 'notFound',
  }
}

/* Caps on what a failed anchor reports back. `MATCH_SCAN_LIMIT` stops counting
 * once the anchor is hopeless — a one-space anchor would otherwise be tallied
 * across the whole note — and `MAX_REPORTED_MATCHES` bounds how many snippets
 * travel back to the model, so a placeholder repeated in every discipline block
 * returns a few disambiguating windows rather than the document. */
const MATCH_SCAN_LIMIT = 50
const MAX_REPORTED_MATCHES = 5
const CONTEXT_WINDOW_CHARS = 48
// Below this a shared leading slice is coincidence, not a divergence point.
const MIN_REPORTABLE_PREFIX = 12

interface AnchorMatchContext {
  heading: string
  snippet: string
}

interface AnchorMatchScan {
  total: number
  contexts: AnchorMatchContext[]
}

interface FlatMatch {
  start: number
  end: number
}

/*
 * Turn a resolver-level anchor failure into a message the model can act on in
 * one more hop instead of guessing a fresh anchor blind.
 *
 * `findUniqueTextRange` deliberately stays cheap — it short-circuits at the
 * second match, and the reconcile and dropped-suggestion paths call it on every
 * document change. The expensive half (counting every match, quoting the text
 * around each) lives here and runs only when an operation is being reported to
 * the model as failed.
 *
 * The failures get different advice on purpose: an ambiguous anchor needs
 * EXTENDING, a missing one cannot be fixed by extending at all, and one
 * carrying markup cannot be fixed by either — only by dropping the tags.
 */
export function describeAnchorFailure(
  editor: Editor,
  needle: string,
  failure: AnchorFailure
): string {
  if (!needle) {
    return failure.error
  }
  return failure.reason === 'ambiguous'
    ? describeAmbiguousAnchor(editor, needle)
    : describeMissingAnchor(editor, needle)
}

function describeAmbiguousAnchor(editor: Editor, needle: string): string {
  const { total, contexts } = scanAnchorMatches(editor, needle)
  const count = total >= MATCH_SCAN_LIMIT ? `${MATCH_SCAN_LIMIT}+` : `${total}`
  const shown =
    contexts.length < total
      ? `Showing the first ${contexts.length} of ${count} matches:`
      : 'Matched here:'
  const lines = [
    `text "${truncate(needle)}" matches ${count} places — it must match exactly one. Extend it with the VISIBLE text of the line next to the one you mean — the discipline label or heading just above it (e.g. "PT: (enter name)"), or the line just below it — joined by a single newline where the line break is. Never copy HTML tags from readEditor into the anchor: tags are not part of the document's text and match nothing. ${shown}`,
  ]
  contexts.forEach((context, index) => {
    const where = context.heading
      ? `under "${truncate(context.heading)}" — `
      : ''
    lines.push(`${index + 1}. ${where}${context.snippet}`)
  })
  return lines.join('\n')
}

function describeMissingAnchor(editor: Editor, needle: string): string {
  const sanitized = sanitizeAnchorNeedle(needle)
  /* Markup gets its own diagnosis. "Matches 0 places — shorten it" is true
   * but useless here: it sends the model back to the bare, ambiguous anchor and
   * closes the loop. Naming the markup is the only advice that leaves it. */
  if (sanitized.hadTags) {
    return [
      `text "${truncate(needle)}" contains HTML tags, and the document's text does not — so this anchor can never match, however long or short it is.`,
      'Anchors are plain visible text only: no tags, no entities. Send the words a reader would see, and where readEditor showed a block boundary (</p>, </li>, <br>) put a single newline instead of the tags.',
      `Stripped of its markup, this anchor reads: "${truncate(collapseWhitespace(sanitized.text), 80)}".`,
    ].join(' ')
  }
  const parts = [
    `text "${truncate(needle)}" matches 0 places — the document holds no such text, so a LONGER anchor cannot help; a corrected or shorter one can. Call readEditor and copy the anchor verbatim from the document text.`,
  ]
  const prefix = longestMatchingPrefix(editor, needle)
  if (prefix) {
    parts.push(
      `The document does contain "${truncateStart(prefix)}" — the anchor stops matching immediately after that.`
    )
  }
  parts.push(
    'Likely causes: whitespace differing from the document (a doubled space, or a line break inside the anchor), typographic characters in the document (curly quotes, en/em dashes) where the anchor used ASCII, or an anchor reaching across an inline chip — a mention chip or a choice field reads as plain text but contributes no characters to the document text.'
  )
  return parts.join(' ')
}

/* Count the anchor's matches and capture a window of text around the first few.
 * Mirrors `findUniqueTextRange`'s two passes (exact, then normalized only when
 * exact found nothing) so the diagnosis describes the same matches the resolver
 * rejected. */
function scanAnchorMatches(editor: Editor, needle: string): AnchorMatchScan {
  const exact = runAnchorScan(editor, (text) => findFlatMatches(text, needle))
  if (exact.total > 0) {
    return exact
  }
  const normalized = normalizeForMatch(needle).text
  if (!normalized) {
    return exact
  }
  return runAnchorScan(editor, (text) =>
    findNormalizedFlatMatches(text, normalized)
  )
}

function runAnchorScan(
  editor: Editor,
  matchesIn: (text: string) => FlatMatch[]
): AnchorMatchScan {
  const contexts: AnchorMatchContext[] = []
  let total = 0
  let heading = ''
  editor.state.doc.descendants((node, pos) => {
    if (total >= MATCH_SCAN_LIMIT) {
      return false
    }
    if (!node.isTextblock) {
      return true
    }
    if (node.type.name === 'heading' && (node.attrs.level ?? 1) <= 2) {
      heading = node.textContent
    }
    const { text } = flattenTextblock(node, pos)
    for (const match of matchesIn(text)) {
      total += 1
      if (contexts.length < MAX_REPORTED_MATCHES) {
        contexts.push({ heading, snippet: snippetAround(text, match) })
      }
      if (total >= MATCH_SCAN_LIMIT) {
        break
      }
    }
    return false
  })
  return { total, contexts }
}

function findFlatMatches(text: string, needle: string): FlatMatch[] {
  const out: FlatMatch[] = []
  let searchFrom = 0
  while (out.length < MATCH_SCAN_LIMIT) {
    const idx = text.indexOf(needle, searchFrom)
    if (idx === -1) {
      return out
    }
    out.push({ start: idx, end: idx + needle.length })
    searchFrom = idx + needle.length
  }
  return out
}

function findNormalizedFlatMatches(text: string, needle: string): FlatMatch[] {
  const haystack = normalizeForMatch(text)
  return findFlatMatches(haystack.text, needle).map((match) => ({
    start: haystack.sourceStart[match.start],
    end: haystack.sourceEnd[match.end - 1],
  }))
}

/* The matched span plus a window of the text on either side, with the match
 * itself bracketed so the model can see which characters it hit and what it can
 * extend into. */
function snippetAround(text: string, match: FlatMatch): string {
  const from = Math.max(0, match.start - CONTEXT_WINDOW_CHARS)
  const to = Math.min(text.length, match.end + CONTEXT_WINDOW_CHARS)
  const lead = from > 0 ? '…' : ''
  const tail = to < text.length ? '…' : ''
  const matched = truncate(text.slice(match.start, match.end))
  const before = text.slice(from, match.start)
  const after = text.slice(match.end, to)
  return `${lead}${before}[[${matched}]]${after}${tail}`
}

/* The longest leading slice of the anchor that IS present, which names the
 * character where anchor and document diverge. Prefix presence is monotone — if
 * a prefix of length n is absent every longer one is too — so a binary search
 * over lengths settles it in a handful of scans. */
function longestMatchingPrefix(editor: Editor, needle: string): string {
  const haystack = documentFlatText(editor)
  let low = MIN_REPORTABLE_PREFIX
  let high = needle.length
  let best = 0
  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    if (haystack.includes(needle.slice(0, mid))) {
      best = mid
      low = mid + 1
    } else {
      high = mid - 1
    }
  }
  return best >= MIN_REPORTABLE_PREFIX ? needle.slice(0, best) : ''
}

function documentFlatText(editor: Editor): string {
  const blocks: string[] = []
  editor.state.doc.descendants((node, pos) => {
    if (!node.isTextblock) {
      return true
    }
    blocks.push(flattenTextblock(node, pos).text)
    return false
  })
  return blocks.join('\n')
}

function collectTextblockMatches(
  editor: Editor,
  visit: (block: PMNode, blockPos: number, out: TextRange[]) => void
): TextRange[] {
  const matches: TextRange[] = []
  editor.state.doc.descendants((node, pos) => {
    if (!node.isTextblock) {
      return true
    }
    visit(node, pos, matches)
    return matches.length <= 1
  })
  return matches
}

interface FlatTextblock {
  text: string
  charToDocPos: number[]
}

// Flat text of a textblock plus a parallel array mapping each character index
// back to its absolute doc position. Inline atoms (mentions, signature buttons)
// contribute no characters but advance the doc position, so a naive
// `pos + 1 + idx` mapping would drift after any such node. A hard break is the
// one non-text child that does contribute a character: a reader sees a line
// break there, `readEditor` shows one, and the anchor guidance tells the model
// to put a newline where a `<br>` was, so the matcher has to see one too.
function flattenTextblock(block: PMNode, blockPos: number): FlatTextblock {
  let text = ''
  const charToDocPos: number[] = []
  let cursor = blockPos + 1
  block.forEach((child) => {
    if (child.isText) {
      const childText = child.text ?? ''
      for (let i = 0; i < childText.length; i += 1) {
        text += childText[i]
        charToDocPos.push(cursor + i)
      }
      cursor += childText.length
    } else {
      if (child.type.name === HARD_BREAK_NODE) {
        text += '\n'
        charToDocPos.push(cursor)
      }
      cursor += child.nodeSize
    }
  })
  return { text, charToDocPos }
}

function findInTextblock(
  block: PMNode,
  blockPos: number,
  needle: string,
  out: TextRange[]
): void {
  const { text, charToDocPos } = flattenTextblock(block, blockPos)
  if (!text) {
    return
  }
  let searchFrom = 0
  while (true) {
    const idx = text.indexOf(needle, searchFrom)
    if (idx === -1) {
      return
    }
    const from = charToDocPos[idx]
    const to = charToDocPos[idx + needle.length - 1] + 1
    out.push({ from, to })
    if (out.length > 1) {
      return
    }
    searchFrom = idx + needle.length
  }
}

function findNormalizedInTextblock(
  block: PMNode,
  blockPos: number,
  needle: string,
  out: TextRange[]
): void {
  const { text, charToDocPos } = flattenTextblock(block, blockPos)
  if (!text) {
    return
  }
  const haystack = normalizeForMatch(text)
  let searchFrom = 0
  while (true) {
    const idx = haystack.text.indexOf(needle, searchFrom)
    if (idx === -1) {
      return
    }
    // Back through both maps: normalized index → original flat-text index →
    // doc position. `sourceEnd` is exclusive, so the last original character
    // covered by the match is at `sourceEnd - 1`.
    const sourceStart = haystack.sourceStart[idx]
    const sourceEnd = haystack.sourceEnd[idx + needle.length - 1]
    out.push({
      from: charToDocPos[sourceStart],
      to: charToDocPos[sourceEnd - 1] + 1,
    })
    if (out.length > 1) {
      return
    }
    searchFrom = idx + needle.length
  }
}

interface NormalizedText {
  text: string
  // Per normalized character, the [sourceStart, sourceEnd) span of source
  // characters it was produced from. A collapsed whitespace run spans the whole
  // run; a composed character spans its base plus its combining marks.
  sourceStart: number[]
  sourceEnd: number[]
}

interface QuoteFoldsLookup {
  [key: string]: string
}

const QUOTE_FOLDS: QuoteFoldsLookup = {
  '‘': "'",
  '’': "'",
  '‚': "'",
  '‛': "'",
  '“': '"',
  '”': '"',
  '„': '"',
  '‟': '"',
}

const WHITESPACE = /\s/
const COMBINING_MARK = /\p{M}/u

// Fold away differences a reader would not see: whitespace runs (including
// non-breaking spaces and newlines) collapse to one space with the ends
// trimmed, curly quotes become straight, decomposed characters compose to NFC,
// and case is ignored. Case-folding is safe here because it can only ever turn
// a miss into a hit or into the ambiguity error, never into a silent wrong
// pick — the caller resolves this pass only when exact matching found nothing,
// and still refuses a non-unique result.
function normalizeForMatch(source: string): NormalizedText {
  const chars: string[] = []
  const sourceStart: number[] = []
  const sourceEnd: number[] = []
  let i = 0
  let pendingSpaceFrom = -1
  while (i < source.length) {
    if (WHITESPACE.test(source[i])) {
      if (pendingSpaceFrom === -1) {
        pendingSpaceFrom = i
      }
      i += 1
      continue
    }
    if (pendingSpaceFrom !== -1) {
      // Leading whitespace is dropped rather than emitted; trailing whitespace
      // is dropped by never flushing a pending run at the end.
      if (chars.length > 0) {
        chars.push(' ')
        sourceStart.push(pendingSpaceFrom)
        sourceEnd.push(i)
      }
      pendingSpaceFrom = -1
    }
    let end = i + 1
    while (end < source.length && COMBINING_MARK.test(source[end])) {
      end += 1
    }
    const folded = foldCluster(source.slice(i, end))
    for (let k = 0; k < folded.length; k += 1) {
      chars.push(folded[k])
      sourceStart.push(i)
      sourceEnd.push(end)
    }
    i = end
  }
  return { text: chars.join(''), sourceStart, sourceEnd }
}

function foldCluster(cluster: string): string {
  const composed = cluster.normalize('NFC')
  let folded = ''
  for (let i = 0; i < composed.length; i += 1) {
    folded += QUOTE_FOLDS[composed[i]] ?? composed[i]
  }
  return folded.toLowerCase()
}

/*
 * Anchor markup, and why it is stripped off the needle only.
 *
 * An anchor is contract-bound to be the document's plain visible text, but
 * `readEditor` hands the model HTML — so a model told to extend an ambiguous
 * anchor extends it with the tags it just read, in good faith. Tags and
 * entities never appear in a textblock's flat text, so the extended anchor
 * matched nothing, and the "not found" advice (shorten it) sent the model
 * straight back to the ambiguous anchor it started from. That loop is what
 * these helpers break.
 *
 * Only the needle is stripped. Document text is already plain, and folding
 * `<`/`>` runs out of a clinician's note would rewrite what they wrote and
 * detach every offset from its source character. Both sides still go through
 * the one shared matcher, `normalizeForMatch`; stripping is input repair on the
 * needle, ahead of that single normalizer, not a second one beside it.
 */
const BLOCK_LEVEL_TAG_NAMES = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'br',
  'dd',
  'div',
  'dl',
  'dt',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'li',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul',
])

/* Deliberately narrow: a name character must follow `<` or `</`, so prose like
 * "a < b > c" or "sats < 90" keeps every character. */
const HTML_TAG_PATTERN = /<\/?([a-zA-Z][a-zA-Z0-9-]*)(?:\s[^<>]*?)?\/?>/g
const HTML_ENTITY_PATTERN =
  /&(#[0-9]+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g

interface NamedEntitiesLookup {
  [key: string]: string
}

const NAMED_ENTITIES: NamedEntitiesLookup = {
  amp: '&',
  apos: "'",
  gt: '>',
  ldquo: '\u201C',
  lsquo: '\u2018',
  lt: '<',
  mdash: '\u2014',
  ndash: '\u2013',
  nbsp: '\u00A0',
  quot: '"',
  rdquo: '\u201D',
  rsquo: '\u2019',
}

function decodeEntities(source: string): string {
  return source.replace(HTML_ENTITY_PATTERN, (whole, body: string) => {
    if (!body.startsWith('#')) {
      return NAMED_ENTITIES[body.toLowerCase()] ?? whole
    }
    const hex = body[1] === 'x' || body[1] === 'X'
    const code = Number.parseInt(
      hex ? body.slice(2) : body.slice(1),
      hex ? 16 : 10
    )
    return Number.isInteger(code) && code > 0 && code <= 0x10ffff
      ? String.fromCodePoint(code)
      : whole
  })
}

interface SanitizedAnchor {
  // The needle with tags removed and entities decoded. A block-level tag leaves
  // a newline behind, so the line breaks the model asserted survive.
  text: string
  // Tags specifically, which is what the failure message names. An anchor that
  // only carried entities is repaired the same way but diagnosed differently.
  hadTags: boolean
  // The normalized runs of visible text between those line breaks.
  segments: string[]
  // The same runs before normalization, index for index with `segments`. Only
  // a failure message reads these: normalized text is lowercased and
  // whitespace-collapsed, which is the wrong thing to quote back at a model
  // being asked to resend a line.
  displaySegments: string[]
}

function sanitizeAnchorNeedle(needle: string): SanitizedAnchor {
  let hadTags = false
  const stripped = needle.replace(HTML_TAG_PATTERN, (_whole, name: string) => {
    hadTags = true
    return BLOCK_LEVEL_TAG_NAMES.has(name.toLowerCase()) ? '\n' : ''
  })
  const text = decodeEntities(stripped)
  const lines = text
    .split('\n')
    .map((line) => ({
      display: line.trim(),
      normalized: normalizeForMatch(line).text,
    }))
    .filter((line) => line.normalized.length > 0)
  return {
    text,
    hadTags,
    segments: lines.map((line) => line.normalized),
    displaySegments: lines.map((line) => line.display),
  }
}

// One visible line of the document: a textblock, or the run between two hard
// breaks inside one. A line never crosses a textblock boundary.
interface DocLine {
  flat: FlatTextblock
  normalized: NormalizedText
}

/* The document as the lines a reader sees, in order. A textblock holding hard
 * breaks yields one line per run, so an anchor that names the lines on either
 * side of a `<br>` is read the same way as one naming two paragraphs — which is
 * what the anchor guidance promises. Blank lines are left out, mirroring the
 * empty-segment filter in `sanitizeAnchorNeedle`. `readEditor` renders a
 * paragraph break and a `<br><br>` as the same blank line and drops an empty
 * paragraph entirely, so a blank line in an anchor names no line of the
 * document and there is nothing for it to match against. */
function collectDocLines(editor: Editor): DocLine[] {
  return collectNodeLines(editor.state.doc)
}

// The lines of any node holding textblocks, read exactly as `collectDocLines`
// reads the live document. Parsed `newContent` goes through here so the trim
// comparison sees the same lines the anchor was matched against.
function collectNodeLines(root: PMNode): DocLine[] {
  const lines: DocLine[] = []
  root.descendants((node, pos) => {
    if (!node.isTextblock) {
      return true
    }
    const { text, charToDocPos } = flattenTextblock(node, pos)
    let lineStart = 0
    for (let i = 0; i <= text.length; i += 1) {
      if (i < text.length && text[i] !== '\n') {
        continue
      }
      const flat = {
        text: text.slice(lineStart, i),
        charToDocPos: charToDocPos.slice(lineStart, i),
      }
      const normalized = normalizeForMatch(flat.text)
      if (normalized.text.length > 0) {
        lines.push({ flat, normalized })
      }
      lineStart = i + 1
    }
    return false
  })
  return lines
}

/* Every line index where the segments land on consecutive lines in the
 * arrangement the anchor's line breaks described: the first segment flush
 * against its line's end, the last flush against its line's start, and any
 * segment between them filling its line exactly. Stops at two — the caller
 * only needs "one" from "more than one". */
function findSegmentSequences(blocks: DocLine[], segments: string[]): number[] {
  const starts: number[] = []
  const last = segments.length - 1
  for (let i = 0; i + last < blocks.length; i += 1) {
    if (!blocks[i].normalized.text.endsWith(segments[0])) {
      continue
    }
    let middleFits = true
    for (let k = 1; k < last; k += 1) {
      if (blocks[i + k].normalized.text !== segments[k]) {
        middleFits = false
        break
      }
    }
    if (!middleFits) {
      continue
    }
    if (!blocks[i + last].normalized.text.startsWith(segments[last])) {
      continue
    }
    starts.push(i)
    if (starts.length > 1) {
      return starts
    }
  }
  return starts
}

// Whether a segment stands on its own as an anchor. Stops at two matches.
function isRepeatedSegment(blocks: DocLine[], segment: string): boolean {
  let total = 0
  for (const block of blocks) {
    let searchFrom = 0
    while (true) {
      const idx = block.normalized.text.indexOf(segment, searchFrom)
      if (idx === -1) {
        break
      }
      total += 1
      if (total > 1) {
        return true
      }
      searchFrom = idx + segment.length
    }
  }
  return false
}

function normalizedRangeIn(
  block: DocLine,
  normStart: number,
  length: number
): TextRange {
  const { flat, normalized } = block
  const sourceStart = normalized.sourceStart[normStart]
  const sourceEnd = normalized.sourceEnd[normStart + length - 1]
  return {
    from: flat.charToDocPos[sourceStart],
    to: flat.charToDocPos[sourceEnd - 1] + 1,
  }
}

/*
 * Resolve an anchor that spans several lines.
 *
 * The range returned NEVER crosses a line boundary, and so never a textblock
 * boundary either: handing a cross-block range to `insertContentAt` merges the
 * blocks and orphans whatever of the last one the anchor did not name, which on
 * a care plan silently rewrites template text the clinician never touched. So
 * a multi-line anchor is read as what it actually is — one repeated target plus
 * the neighbouring lines that pin it down — and only the target's own line is
 * returned. A line is what a reader sees as one: a `<br>` splits a line just as
 * a paragraph break does.
 *
 * Which segment is the target is not guessed. The whole sequence must sit in
 * exactly one place in the document, and exactly one of its segments must be
 * the one that repeats; the segments that already resolve on their own needed
 * no disambiguating and are therefore the context. Anything else — no such
 * sequence, two such sequences, no repeated segment, or several — is left
 * unresolved or reported as ambiguous rather than placed on a guess.
 */
function resolveSegmentedAnchor(
  editor: Editor,
  anchor: SanitizedAnchor
): TextRange | 'ambiguous' | 'unresolved' {
  const { segments } = anchor
  const blocks = collectDocLines(editor)
  const starts = findSegmentSequences(blocks, segments)
  if (starts.length === 0) {
    return 'unresolved'
  }
  if (starts.length > 1) {
    return 'ambiguous'
  }
  const repeated = segments.filter((segment) =>
    isRepeatedSegment(blocks, segment)
  )
  if (repeated.length !== 1) {
    return 'unresolved'
  }
  const targetIndex = segments.indexOf(repeated[0])
  const block = blocks[starts[0] + targetIndex]
  const segment = segments[targetIndex]
  // The sequence check pins where in its line the target sits: the first
  // segment ends the line, every later one starts it.
  const normStart =
    targetIndex === 0 ? block.normalized.text.length - segment.length : 0
  return {
    ...normalizedRangeIn(block, normStart, segment.length),
    segmentContext: {
      before: anchorSegments(anchor, 0, targetIndex),
      after: anchorSegments(anchor, targetIndex + 1, segments.length),
    },
  }
}

function anchorSegments(
  anchor: SanitizedAnchor,
  from: number,
  to: number
): AnchorSegment[] {
  const out: AnchorSegment[] = []
  for (let i = from; i < to; i += 1) {
    out.push({
      normalized: anchor.segments[i],
      display: anchor.displaySegments[i],
    })
  }
  return out
}

/*
 * Drop the anchor's context lines back off `newContent`.
 *
 * A multi-line anchor resolves to its one repeated target line, with the
 * neighbouring lines as context that pins it down (see
 * `resolveSegmentedAnchor`). That reading came from care plans, where the model
 * disambiguates a repeated placeholder and sends only the placeholder's
 * replacement. On a visit note models read `replaceText` the other way round:
 * `findText` is the span and `newContent` is that whole span rewritten. Staging
 * such content over the target line alone leaves the header, the instruction
 * and the examples in the document twice — the wrong end state, silently.
 *
 * So when `newContent` opens with the context lines that precede the target, or
 * closes with the ones that follow it, those lines are dropped and what remains
 * is staged against the same target-only range. Both readings then land the end
 * state the model described, and neither duplicates a line.
 *
 * `newContent` that does not repeat the context at all comes through untouched,
 * byte for byte — the care-plan shape, and what every suggestion already staged
 * on a note re-resolves to when the note is reopened.
 *
 * Anything in between is refused rather than guessed at: some context lines
 * repeated and not others, a context line genuinely reworded, a context line
 * turning up in the middle of the content. Each of those has two plausible
 * readings and one of them rewrites template text nobody asked to change.
 *
 * One case stays undetectable, and it is the single-context-line anchor whose
 * sole context line the model rewrote past recognition — "PT: (enter name)"
 * sent back as "PT: Jane Doe, PT". No original context line survives in the
 * content, so nothing distinguishes it from content that never repeated the
 * context at all, and that content is where a legitimate multi-paragraph
 * replacement lives. Both readings are staged whole, exactly as they were
 * before any of this existed. The lever that reaches it is the tool contract,
 * which tells the model not to restate or edit a neighbouring line — not a
 * similarity guess here, which would fail legitimate content and would change
 * how an already-staged suggestion re-resolves when a note is reopened.
 */
function trimReSuppliedContext(
  editor: Editor,
  html: string,
  context: SegmentedAnchorContext
): { html: string } | { error: string } {
  const content = editor.schema.nodeFromJSON(
    generateJSON(html, editor.extensionManager.extensions)
  )
  const lines = collectNodeLines(content)
  if (lines.length === 0) {
    return { html }
  }
  const keys = lines.map((line) => contextKey(line.normalized.text))
  const leading = edgeRepeat(keys, context.before, 'leading')
  const trailing = edgeRepeat(keys, context.after, 'trailing')
  if (leading === 'partial' || trailing === 'partial') {
    return { error: describeContextRepeat(context) }
  }
  if (leading === 'absent' && trailing === 'absent') {
    return { html }
  }
  const first = leading === 'repeated' ? context.before.length : 0
  const last =
    lines.length - 1 - (trailing === 'repeated' ? context.after.length : 0)
  if (first > last) {
    return { error: describeContextRepeat(context) }
  }
  const cut = content.cut(lineStart(lines[first]), lineEnd(lines[last]))
  return {
    html: generateHTML(
      cut.toJSON() as JSONContent,
      editor.extensionManager.extensions
    ),
  }
}

function lineStart(line: DocLine): number {
  return line.flat.charToDocPos[0]
}

function lineEnd(line: DocLine): number {
  const { charToDocPos } = line.flat
  return charToDocPos[charToDocPos.length - 1] + 1
}

type EdgeRepeat = 'absent' | 'repeated' | 'partial'

/* Whether the content re-supplies one edge's context lines: `repeated` is the
 * whole run, in order, flush against that edge; `absent` is none of them
 * anywhere in the content; everything else — a short run, a reworded line, one
 * of them stranded in the middle — is `partial`, and fails. */
function edgeRepeat(
  contentKeys: string[],
  segments: AnchorSegment[],
  edge: 'leading' | 'trailing'
): EdgeRepeat {
  if (segments.length === 0) {
    return 'absent'
  }
  const keys = segments.map((segment) => contextKey(segment.normalized))
  const slice =
    edge === 'leading'
      ? contentKeys.slice(0, keys.length)
      : contentKeys.slice(Math.max(0, contentKeys.length - keys.length))
  if (
    slice.length === keys.length &&
    keys.every((key, index) => key === slice[index])
  ) {
    return 'repeated'
  }
  return keys.some((key) => contentKeys.includes(key)) ? 'partial' : 'absent'
}

/* Normalized text with its whitespace folded out entirely. This comparison is
 * the one place that goes further than `normalizeForMatch`: a heading re-typed
 * as "3. Cognitive Status" where the note holds "3.Cognitive Status" is the
 * same line to a reader, and failing an edit over that space would be refusing
 * one whose meaning is not in doubt. It stays local to the context check —
 * anchor placement still runs on `normalizeForMatch`, which never drops a
 * character from the span it matches. */
function contextKey(normalized: string): string {
  return normalized.replace(/\s+/g, '')
}

function describeContextRepeat(context: SegmentedAnchorContext): string {
  const quoted = [...context.before, ...context.after]
    .map((segment) => `"${truncate(segment.display)}"`)
    .join(', ')
  return [
    `newContent repeats the anchor's neighbouring line(s) — ${quoted} — but not in a form that can be matched and dropped.`,
    'A findText spanning several lines replaces ONLY the repeated target line; the other lines are context that pins that line down and stays in the document untouched.',
    'Send newContent for the target line alone with those lines removed, or re-supply them verbatim so they can be trimmed off.',
    'A context line cannot be edited in the same operation — anchor a second replaceText on that line instead.',
  ].join(' ')
}

// Find the end of a heading's "section" — just before the next heading at the same or higher level.
export function findSectionEnd(
  editor: Editor,
  headingText: string
): { position: number } | { error: string } {
  const headings: { pos: number; level: number; text: string }[] = []
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'heading') {
      headings.push({
        pos,
        level: node.attrs.level ?? 1,
        text: node.textContent,
      })
    }
    return true
  })
  const matches = headings.filter((h) => h.text === headingText)
  if (matches.length === 0) {
    return {
      error: `heading "${truncate(headingText)}" not found`,
    }
  }
  if (matches.length > 1) {
    return {
      error: `heading "${truncate(headingText)}" matches multiple headings — make it unique`,
    }
  }
  const target = matches[0]
  const nextSibling = headings.find(
    (h) => h.pos > target.pos && h.level <= target.level
  )
  if (nextSibling) {
    return { position: nextSibling.pos }
  }
  // When no same-or-higher-level heading follows, stop before any trailing h3
  // that matches the template version stamp (e.g. "Template: V5 3/13/26"), so
  // that appendToSection inserts above the footer rather than below it.
  const trailingVersionStamp = headings.find(
    (h) => h.pos > target.pos && /^Template:/i.test(h.text.trim())
  )
  if (trailingVersionStamp) {
    return { position: trailingVersionStamp.pos }
  }
  return { position: editor.state.doc.content.size }
}

/* Parse an inline choice node's `data-options` ("A|B|C") into the agent-facing
 * option list. Mirrors ActionableChoice's split — the value the agent passes to
 * setChoiceById must be one of these raw option strings. */
function parseChoiceOptions(options: JsonValue | undefined): string[] {
  if (typeof options !== 'string' || options.length === 0) {
    return []
  }
  return options.split('|')
}

/* Walk the document in order and emit every `choice` field with the context the
 * agent needs to target it: its durable fieldId, allowed options, current
 * value, the label/text sharing its block, and the H2 section it sits under.
 * Choice fields are inline atoms with no text content, so they never appear in
 * the markdown summary — this is the only surface that exposes them. */
export function collectChoiceFields(editor: Editor): ReadEditorChoiceField[] {
  const fields: ReadEditorChoiceField[] = []
  let currentHeading = ''

  editor.state.doc.descendants((node) => {
    if (node.type.name === 'heading') {
      /* Only H1/H2 are treated as section headings the agent targets; track the
       * nearest one. Deeper sub-headings (H3+) don't update the section label,
       * but still `return false` here so they never fall through to the
       * textblock branch below and leave choice fields under them carrying a
       * stale heading. */
      if ((node.attrs.level ?? 1) <= 2) {
        currentHeading = node.textContent
      }
      return false
    }
    if (!node.isTextblock) {
      return true
    }
    /* The choice field's "nearby text" is the rest of its enclosing block —
     * typically the label that precedes it on the same line. */
    const blockText = node.textContent.trim()
    node.forEach((child) => {
      if (child.type.name !== 'choice') {
        return
      }
      const display = child.attrs.display
      fields.push({
        fieldId: (child.attrs.fieldId as string | null) ?? null,
        options: parseChoiceOptions(child.attrs.options),
        currentValue:
          typeof display === 'string' && display.length > 0 ? display : null,
        nearbyText: blockText,
        heading: currentHeading,
      })
    })
    /* Don't descend further: a textblock's children are inline (we already
     * iterated them) and never contain headings or nested choice fields. */
    return false
  })

  return fields
}

/**
 * Normalize a taskItem label for stable text matching: trim, collapse whitespace.
 * @param text - The raw taskItem label text content.
 * @returns The trimmed label with internal whitespace runs collapsed to single spaces.
 */
function normalizeTaskLabel(text: string): string {
  return text.trim().replace(/\s+/g, ' ')
}

/* Walk the document and collect every taskItem node in document order, tagged
 * with its enclosing H1 or H2 section heading. Used by readEditor to surface them
 * in the `taskItems` array and by resolveSetTaskItemChecked for targeting. */
export function collectTaskItems(editor: Editor): ReadEditorTaskItem[] {
  const items: ReadEditorTaskItem[] = []
  let currentHeading = ''

  editor.state.doc.descendants((node) => {
    if (node.type.name === 'heading') {
      if ((node.attrs.level ?? 1) <= 2) {
        currentHeading = node.textContent
      }
      return false
    }
    if (node.type.name === 'taskItem') {
      items.push({
        label: normalizeTaskLabel(node.textContent),
        checked: node.attrs.checked === true,
        heading: currentHeading,
      })
      return false
    }
    return true
  })

  return items
}

/* Find a taskItem node by section heading + normalized label text, returning
 * its document position range, or an error string when unmatched or ambiguous. */
export function findTaskItemNode(
  editor: Editor,
  sectionHeading: string | null,
  labelText: string
):
  | { from: number; to: number; node: PMNode; checked: boolean }
  | { error: string } {
  const normalized = normalizeTaskLabel(labelText)
  let currentHeading = ''
  const matches: {
    from: number
    to: number
    node: PMNode
    checked: boolean
  }[] = []

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'heading') {
      if ((node.attrs.level ?? 1) <= 2) {
        currentHeading = node.textContent
      }
      return false
    }
    if (node.type.name === 'taskItem') {
      const inSection =
        sectionHeading === null || currentHeading === sectionHeading
      if (inSection && normalizeTaskLabel(node.textContent) === normalized) {
        matches.push({
          from: pos,
          to: pos + node.nodeSize,
          node,
          checked: node.attrs.checked === true,
        })
      }
      return false
    }
    return true
  })

  if (matches.length === 0) {
    const scope =
      sectionHeading !== null ? ` in section "${truncate(sectionHeading)}"` : ''
    return {
      error: `setTaskItemChecked: no taskItem with label "${truncate(normalized)}"${scope} found`,
    }
  }
  if (matches.length > 1) {
    return {
      error: `setTaskItemChecked: label "${truncate(normalized)}" matches ${matches.length} taskItems — add a sectionHeading to disambiguate`,
    }
  }
  return matches[0]
}

/* Build the HTML for a taskItem node with its checked state set to the target
 * value. Emits only the <li> — no outer <ul> wrapper — so that insertContentAt
 * replaces the existing taskItem in-place without introducing a nested taskList. */
function buildTaskItemHtml(node: PMNode, checked: boolean): string {
  const checkedAttr = checked ? ' data-checked="true"' : ' data-checked="false"'
  const text = escapeHtml(node.textContent)
  return `<li data-type="taskItem"${checkedAttr}>${text}</li>`
}

function resolveSetTaskItemChecked(
  editor: Editor,
  sectionHeading: string | null,
  labelText: string,
  checked: boolean
): ResolvedOperation | { error: string } {
  const target = findTaskItemNode(editor, sectionHeading, labelText)
  if ('error' in target) {
    return target
  }
  if (target.checked === checked) {
    return {
      error: `setTaskItemChecked: taskItem "${truncate(labelText)}" is already ${checked ? 'checked' : 'unchecked'} — no change needed`,
    }
  }
  return {
    kind: 'replace',
    from: target.from,
    to: target.to,
    htmlContent: buildTaskItemHtml(target.node, checked),
    label: `${checked ? 'Check' : 'Uncheck'} "${truncate(normalizeTaskLabel(labelText))}"`,
    render: { type: 'taskItem' },
  }
}

/* Locate a choice node by its durable fieldId. Returns the node and its
 * inclusive doc range, or null when no choice node carries that id (e.g. the
 * field was deleted, or the id is stale). Modeled on findFieldNodeAfter. */
function findChoiceNodeByFieldId(
  editor: Editor,
  fieldId: string
): { from: number; to: number; node: PMNode } | null {
  let found: { from: number; to: number; node: PMNode } | null = null
  editor.state.doc.descendants((node, pos) => {
    if (found) {
      return false
    }
    if (node.type.name === 'choice' && node.attrs.fieldId === fieldId) {
      found = { from: pos, to: pos + node.nodeSize, node }
      return false
    }
    return true
  })
  return found
}

/* Serialize a choice button preserving its identifying attributes and setting
 * only the displayed value — matching the HTML the agent would hand to
 * replaceNode, so it routes through the identical stage/accept path. The value
 * is the button's text content (how ChoiceNode.parseHTML reads `display`). */
function buildChoiceButtonHtml(node: PMNode, value: string): string {
  const attrs: string[] = ['data-type="choice"']
  const options = node.attrs.options
  if (typeof options === 'string' && options.length > 0) {
    attrs.push(`data-options="${escapeHtmlAttr(options)}"`)
  }
  const dropdown = node.attrs.dropdown
  if (typeof dropdown === 'string' && dropdown.length > 0) {
    attrs.push(`data-dropdown="${escapeHtmlAttr(dropdown)}"`)
  }
  const fieldId = node.attrs.fieldId
  if (typeof fieldId === 'string' && fieldId.length > 0) {
    attrs.push(`data-field-id="${escapeHtmlAttr(fieldId)}"`)
  }
  return `<button ${attrs.join(' ')}>${escapeHtml(value)}</button>`
}

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// `textContent` with a newline at every hard break. This is the text the model
// copies anchors from, so a `<br>` has to read the same way here as it does in
// `flattenTextblock`, or the anchor the model copies verbatim can never match.
function visibleText(node: PMNode): string {
  return node.textBetween(0, node.content.size, undefined, (leaf) =>
    leaf.type.name === HARD_BREAK_NODE ? '\n' : ''
  )
}

export function editorToMarkdown(editor: Editor): string {
  const lines: string[] = []
  editor.state.doc.descendants((node) => {
    if (node.type.name === 'heading') {
      const level = node.attrs.level ?? 1
      lines.push(`${'#'.repeat(level)} ${visibleText(node)}`)
      return false
    }
    if (node.type.name === 'paragraph') {
      const text = visibleText(node).trim()
      if (text) {
        lines.push(text)
      }
      return false
    }
    if (node.type.name === 'bulletList' || node.type.name === 'orderedList') {
      node.forEach((item) => {
        const prefix = node.type.name === 'orderedList' ? '1.' : '-'
        lines.push(`${prefix} ${visibleText(item).trim()}`)
      })
      return false
    }
    if (node.type.name === 'taskList') {
      node.forEach((item) => {
        const checked = item.attrs.checked ? 'x' : ' '
        lines.push(`- [${checked}] ${visibleText(item).trim()}`)
      })
      return false
    }
    return true
  })
  return lines.join('\n\n')
}

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

function truncate(s: string, max = 40): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s
}

// Keeps the END of a string. An anchor's divergence point sits at the tail of
// the part that did match, so trimming from the front is what preserves it.
function truncateStart(s: string, max = 48): string {
  return s.length > max ? `…${s.slice(s.length - (max - 1))}` : s
}

function buildSummary(
  label: string,
  staged: number,
  failed: number,
  // Of `staged`, those that took the place of an earlier pending suggestion.
  replaced = 0
): string {
  if (staged === 0 && failed === 0) {
    return `No operations were submitted for ${label}.`
  }
  const parts: string[] = []
  if (staged > 0) {
    parts.push(
      `Proposed ${staged} change${staged === 1 ? '' : 's'} in ${label}. The user can accept or reject each one inline.`
    )
  }
  /* Said, so the model does not go looking for a cause when the clinician
   * sees one suggestion where it expected two. */
  if (replaced > 0) {
    parts.push(
      staged === 1
        ? 'It took the place of an earlier pending suggestion of yours in the same place, which the clinician no longer sees.'
        : `${replaced} of them took the place of an earlier pending suggestion of yours in the same place, which the clinician no longer sees.`
    )
  }
  if (failed > 0) {
    parts.push(
      `${failed} operation${failed === 1 ? '' : 's'} failed to resolve — see the failed list for retry guidance.`
    )
  }
  return parts.join(' ')
}
